import { theme } from "../theme";

// Severity banding for the 5×5 likelihood × impact matrix (score = L × I).
// Bands follow standard risk-heatmap practice and align with the platform's
// default risk appetite threshold of 12 (an organisation sets its own: risk.appetite_score):
//   Low 1–4 · Medium 5–9 · High 10–12 · Critical 15–25 (scores 13/14 can't occur)
export type RiskSeverity = "low" | "medium" | "high" | "critical";

/** A risk still carrying exposure — the statuses the register, board report and Analytics count as open. */
export const OPEN_RISK_STATUSES: ReadonlySet<string> = new Set(["open", "under_treatment", "tentative_remediated"]);

export const SEVERITY_ORDER: RiskSeverity[] = ["critical", "high", "medium", "low"];

export function severityOf(score: number): RiskSeverity {
  if (score <= 4) return "low";
  if (score <= 9) return "medium";
  if (score <= 12) return "high";
  return "critical";
}

export const SEVERITY_META: Record<RiskSeverity, { label: string; range: string; text: string; bg: string }> = {
  low:      { label: "Low",      range: "1–4",   text: theme.color.riskLowText,  bg: theme.color.riskLowBg },
  medium:   { label: "Medium",   range: "5–9",   text: theme.color.riskMedText,  bg: theme.color.riskMedBg },
  high:     { label: "High",     range: "10–12", text: theme.color.riskHighText, bg: theme.color.riskHighBg },
  critical: { label: "Critical", range: "15–25", text: theme.color.riskCritText, bg: theme.color.riskCritBg },
};

export function scoreColor(score: number | null | undefined): string {
  if (!score) return theme.color.faint;
  return SEVERITY_META[severityOf(score)].text;
}

export function scoreBg(score: number | null | undefined): string {
  if (!score) return "transparent";
  return SEVERITY_META[severityOf(score)].bg;
}

export function scoreLabel(score: number | null | undefined): string {
  if (!score) return "—";
  return SEVERITY_META[severityOf(score)].label;
}

// Count risks per severity band using the effective score (residual if set,
// otherwise inherent). Unscored risks are reported separately.
export function countBySeverity(
  risks: Array<{ residualScore?: number | null; inherentScore?: number | null }>,
): Record<RiskSeverity, number> & { unscored: number } {
  const counts = { critical: 0, high: 0, medium: 0, low: 0, unscored: 0 };
  for (const r of risks) {
    const s = r.residualScore ?? r.inherentScore ?? 0;
    if (s > 0) counts[severityOf(s)] += 1;
    else counts.unscored += 1;
  }
  return counts;
}
